import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Braces,
  Repeat,
} from 'lucide-react';
import { apiRequest } from '../../config/api';
import { NODE_TYPES } from './nodeTypes';
import NodeOutputInspector from './NodeOutputInspector';

const TRIGGER_NODE_TYPES = new Set(['cron', 'manual', 'workflowTrigger']);

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-100', dot: 'bg-gray-300', label: 'Pending' },
  running: { icon: Loader2, color: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-500', label: 'Running', spin: true },
  completed: { icon: CheckCircle2, color: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-700', label: 'Completed' },
  failed: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-700', label: 'Failed' },
  cancelled: { icon: AlertCircle, color: 'text-gray-500', bg: 'bg-gray-100', dot: 'bg-gray-400', label: 'Cancelled' },
  skipped: { icon: AlertCircle, color: 'text-gray-400', bg: 'bg-gray-100', dot: 'bg-gray-300', label: 'Skipped' },
};

function StatusBadge({ status, size = 'sm' }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  const sizeClasses = size === 'lg'
    ? 'gap-1.5 px-2.5 py-1 text-xs'
    : 'gap-1 px-1.5 py-0.5 text-[11px]';
  return (
    <span className={`inline-flex items-center font-medium rounded-full ${sizeClasses} ${config.bg} ${config.color}`}>
      <Icon className={`${size === 'lg' ? 'w-3.5 h-3.5' : 'w-3 h-3'} ${config.spin ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}

function IterationStatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />;
  if (status === 'failed') return <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />;
  return <Clock className="w-3 h-3 text-gray-300 flex-shrink-0" />;
}

function IterateNodeDetail({ outputData }) {
  const iterations = outputData?.iterations || [];
  const count = outputData?.count ?? 0;
  const executed = outputData?.executed ?? iterations.length;

  if (iterations.length === 0) {
    return (
      <div className="mt-1.5 text-[11px] text-gray-400 italic">
        {count === 0 ? 'No items in list' : `${executed} iteration${executed !== 1 ? 's' : ''} completed`}
      </div>
    );
  }

  return (
    <div className="mt-1.5 space-y-1">
      <div className="text-[10px] text-gray-400 mb-1.5">
        {executed} of {count} item{count !== 1 ? 's' : ''} processed
      </div>
      {iterations.map((iter) => (
        <div
          key={iter.index}
          className="flex items-start gap-1.5 px-2 py-1.5 bg-gray-50 rounded-lg border border-gray-100"
        >
          <IterationStatusIcon status={iter.status} />
          <div className="flex-1 min-w-0">
            <span className="text-[11px] font-medium text-gray-600">
              Item {iter.index + 1}
            </span>
            {iter.error && (
              <p className="text-[10px] text-red-500 mt-0.5 font-mono break-all">{iter.error}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function NodeRunItem({ nodeRun, isLast, style }) {
  const [expanded, setExpanded] = useState(false);
  const [showInspector, setShowInspector] = useState(false);
  const typeDef = NODE_TYPES[nodeRun.node_type];
  const statusConfig = STATUS_CONFIG[nodeRun.status] || STATUS_CONFIG.pending;
  const hasOutput = nodeRun.output_data && Object.keys(nodeRun.output_data).length > 0;
  const hasError = !!nodeRun.error;
  const isIterateNode = nodeRun.node_type === 'iterate';
  const expandable = hasOutput || hasError;

  // For iterate nodes, show iteration count in the header
  const iterateCount = isIterateNode && nodeRun.output_data?.executed != null
    ? nodeRun.output_data.executed
    : null;

  return (
    <div className="flex gap-2.5" style={style}>
      {/* Timeline track */}
      <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusConfig.dot} ${nodeRun.status === 'running' ? 'animate-pulse' : ''}`} />
        {!isLast && <div className="w-px flex-1 bg-gray-200 mt-1" />}
      </div>

      {/* Node content */}
      <div className={`flex-1 min-w-0 ${!isLast ? 'pb-3' : ''}`}>
        <button
          onClick={() => expandable && setExpanded((v) => !v)}
          className={`w-full flex items-center gap-2 text-left group ${expandable ? 'cursor-pointer' : 'cursor-default'}`}
        >
          {typeDef && (
            <div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: typeDef.color || '#94a3b8' }}
            />
          )}
          <span className="text-xs font-medium text-gray-700 truncate flex-1">
            {typeDef?.label || nodeRun.node_type}
          </span>
          {isIterateNode && iterateCount !== null && (
            <span className="flex items-center gap-0.5 text-[10px] text-gray-400 flex-shrink-0">
              <Repeat className="w-2.5 h-2.5" />
              {iterateCount}x
            </span>
          )}
          {expandable && (
            expanded
              ? <ChevronDown className="w-3 h-3 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
              : <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
          )}
        </button>
        {expanded && (
          <div className="mt-1.5 ml-3.5">
            {hasError && (
              <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2 font-mono break-all">
                {nodeRun.error}
              </div>
            )}
            {isIterateNode && hasOutput ? (
              <IterateNodeDetail outputData={nodeRun.output_data} />
            ) : hasOutput && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowInspector(true); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg border transition-colors bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-600"
              >
                <Braces className="w-3 h-3" />
                Inspect output
                <span className="text-[10px] opacity-70">
                  ({Object.keys(nodeRun.output_data).length} key{Object.keys(nodeRun.output_data).length !== 1 ? 's' : ''})
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {showInspector && (
        <NodeOutputInspector
          nodeId={nodeRun.node_id}
          nodeType={nodeRun.node_type}
          outputData={nodeRun.output_data}
          onClose={() => setShowInspector(false)}
        />
      )}
    </div>
  );
}

function RunDetail({ runId, onNodeStatusesUpdate, onBack }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchRun = useCallback(async () => {
    try {
      const resp = await apiRequest(`/workflows/runs/${runId}`);
      if (resp.ok) {
        const data = await resp.json();
        setRun(data);

        if (data.node_runs && onNodeStatusesUpdate) {
          const statuses = {};
          for (const nr of data.node_runs) {
            statuses[nr.node_id] = nr.status;
          }
          onNodeStatusesUpdate(statuses);
        }

        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      }
    } catch (err) {
      console.error('Error fetching run:', err);
    } finally {
      setLoading(false);
    }
  }, [runId, onNodeStatusesUpdate]);

  useEffect(() => {
    setLoading(true);
    fetchRun();
    pollRef.current = setInterval(fetchRun, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [runId, fetchRun]);

  if (loading && !run) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!run) return null;

  const nodeRuns = [...(run.node_runs || [])].sort((a, b) => {
    const aT = TRIGGER_NODE_TYPES.has(a.node_type) ? 0 : 1;
    const bT = TRIGGER_NODE_TYPES.has(b.node_type) ? 0 : 1;
    if (aT !== bT) return aT - bT;
    return (a.started_at || '').localeCompare(b.started_at || '');
  });

  const completedNodes = nodeRuns.filter((n) => n.status === 'completed').length;
  const totalNodes = nodeRuns.length;
  const duration = run.completed_at && run.started_at
    ? Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)
    : null;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Run header card */}
      <div className="flex-shrink-0 border-b border-gray-100">
        <div className="px-3 pt-2 pb-3">
          {/* Back + run ID row */}
          <div className="flex items-center gap-1.5 mb-2.5">
            <button
              onClick={onBack}
              className="p-0.5 rounded text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-[10px] text-gray-400 font-mono">{run.id.slice(0, 8)}</span>
            <span className="text-[10px] text-gray-300">&middot;</span>
            <span className="text-[10px] text-gray-400">{run.trigger_type}</span>
            <div className="flex-1" />
            <StatusBadge status={run.status} size="lg" />
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                run.status === 'failed' ? 'bg-gray-700' :
                run.status === 'running' ? 'bg-gray-500' :
                'bg-gray-700'
              }`}
              style={{ width: `${totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0}%` }}
            />
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-gray-400">
              {completedNodes}/{totalNodes} nodes
            </span>
            {duration !== null && (
              <span className="text-[10px] text-gray-400">
                {duration < 60 ? `${duration}s` : `${Math.floor(duration / 60)}m ${duration % 60}s`}
              </span>
            )}
            <span className="text-[10px] text-gray-400 ml-auto">
              {new Date(run.created_at).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
        </div>

        {/* Error banner */}
        {run.error && (
          <div className="mx-3 mb-3 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2 break-all font-mono">
            {run.error}
          </div>
        )}
      </div>

      {/* Node timeline — staggered entrance */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {nodeRuns.map((nr, idx) => (
          <NodeRunItem
            key={nr.id}
            nodeRun={nr}
            isLast={idx === nodeRuns.length - 1}
            style={{
              animation: 'runNodeFadeIn 0.25s ease-out both',
              animationDelay: `${idx * 60}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function RunListItem({ run, onSelect, style }) {
  const statusConfig = STATUS_CONFIG[run.status] || STATUS_CONFIG.pending;

  return (
    <button
      onClick={onSelect}
      style={style}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors hover:bg-gray-50 border border-transparent active:scale-[0.98]"
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusConfig.dot} ${run.status === 'running' ? 'animate-pulse' : ''}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
          <span className="text-[10px] text-gray-300">&middot;</span>
          <span className="text-[10px] text-gray-400">{run.trigger_type}</span>
        </div>
        <div className="text-[10px] text-gray-400 mt-0.5">
          {new Date(run.created_at).toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
          })}
        </div>
      </div>
      <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
    </button>
  );
}

export default function RunHistoryPanel({
  workflowId,
  activeRunId,
  onSelectRun,
  onNodeStatusesUpdate,
  onClose,
}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRunId, setSelectedRunId] = useState(activeRunId);
  // 'list' or 'detail'
  const [view, setView] = useState(activeRunId ? 'detail' : 'list');
  // 'slide-left' (list→detail) or 'slide-right' (detail→list) or null
  const [transition, setTransition] = useState(null);

  useEffect(() => {
    if (activeRunId) {
      setSelectedRunId(activeRunId);
      if (view === 'list') {
        setTransition('slide-left');
        requestAnimationFrame(() => setView('detail'));
      }
    }
  }, [activeRunId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRuns = useCallback(async () => {
    try {
      const resp = await apiRequest(`/workflows/${workflowId}/runs`);
      if (resp.ok) {
        const data = await resp.json();
        setRuns(data);
      }
    } catch (err) {
      console.error('Error fetching runs:', err);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    fetchRuns();
    const interval = setInterval(fetchRuns, 10000);
    return () => clearInterval(interval);
  }, [fetchRuns]);

  useEffect(() => {
    if (activeRunId) fetchRuns();
  }, [activeRunId, fetchRuns]);

  const handleSelectRun = (runId) => {
    setSelectedRunId(runId);
    onSelectRun(runId);
    setTransition('slide-left');
    requestAnimationFrame(() => setView('detail'));
  };

  const handleBackToList = () => {
    onSelectRun(null);
    onNodeStatusesUpdate({});
    setTransition('slide-right');
    requestAnimationFrame(() => setView('list'));
  };

  // Clear transition flag after animation completes
  const handleTransitionEnd = () => {
    setTransition(null);
    if (view === 'list') {
      setSelectedRunId(null);
    }
  };

  const slideClass = transition === 'slide-left'
    ? 'animate-[slideInFromRight_0.2s_ease-out_both]'
    : transition === 'slide-right'
      ? 'animate-[slideInFromLeft_0.2s_ease-out_both]'
      : '';

  return (
    <div className="w-80 border-l border-gray-100 bg-white flex flex-col overflow-hidden flex-shrink-0">
      {/* Keyframe styles */}
      <style>{`
        @keyframes slideInFromRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes runNodeFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div className="h-10 border-b border-gray-100 flex items-center justify-between px-3 flex-shrink-0">
        <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Run History
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Content — animated view switcher */}
      <div
        className={`flex-1 flex flex-col overflow-hidden ${slideClass}`}
        onAnimationEnd={handleTransitionEnd}
      >
        {view === 'detail' && selectedRunId ? (
          <RunDetail
            runId={selectedRunId}
            onNodeStatusesUpdate={onNodeStatusesUpdate}
            onBack={handleBackToList}
          />
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        ) : runs.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Clock className="w-5 h-5 text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-500">No runs yet</p>
            <p className="text-xs text-gray-400 mt-1 text-center">
              Click "Run" to execute this workflow
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {runs.map((run, idx) => (
              <RunListItem
                key={run.id}
                run={run}
                onSelect={() => handleSelectRun(run.id)}
                style={{
                  animation: transition === 'slide-right' ? `runNodeFadeIn 0.2s ease-out both` : undefined,
                  animationDelay: transition === 'slide-right' ? `${idx * 40}ms` : undefined,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
